import { useCallback, useEffect, useMemo, useState } from 'react';

import { AlertTriangleIcon, ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Chips } from '@/components/atoms/Chip/Chip';
import { MultiSelect } from '@/components/atoms/MultiSelect';
import ToolkitWrapper from '@/components/organisms/Toolkit/ToolkitWrapper';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import useDialogAlert from '@/hooks/useDialogAlert';
import useDialogs from '@/hooks/useDialogs';
import { selectNodeIdByActionId, selectSelectedAction } from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { Action } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { removeDollarSign, sanitizeVariableName } from '@/util/util';

const ToolkitActionReset = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { brainId } = useParams();
  const { getContextVariables } = useDialogs(brainId);
  const contextVariables = getContextVariables(false);
  const [createdVariables, setCreatedVariables] = useState<string[]>([]);

  const { option, variables, actionId, type } = useSelector((state: RootState) => {
    const selectedAction = selectSelectedAction(state) as Extract<Action, { type: 'reset' }>;

    return {
      option: selectedAction?.option,
      variables: selectedAction?.variables,
      actionId: selectedAction?.action_id,
      type: selectedAction?.type,
    };
  }, shallowEqual);
  const parentNodeId = useSelector(selectNodeIdByActionId(actionId));

  const multiSelectOptions = useMemo(() => {
    const options = contextVariables.map((v) => ({
      label: v.label,
      value: removeDollarSign(v.value),
    }));
    const existingValues = new Set(options.map((o) => o.value));
    const newOptions = createdVariables
      .filter((v) => !existingValues.has(v))
      .map((v) => ({ label: `$${v}`, value: v }));
    return [...options, ...newOptions];
  }, [contextVariables, createdVariables]);

  const allOptionValues = useMemo(
    () => multiSelectOptions.map((o) => o.value),
    [multiSelectOptions]
  );

  const selectedValues = useMemo(() => {
    if (option === 'specific' || (!option && variables)) {
      return variables ?? [];
    }
    return allOptionValues;
  }, [option, variables, allOptionValues]);

  const handleSelectionChange = useCallback(
    (newSelectedValues: string[]) => {
      if (newSelectedValues.length === allOptionValues.length) {
        dispatch(updateAction({ actionId, action: { option: 'all', variables: undefined } }));
      } else {
        dispatch(
          updateAction({ actionId, action: { option: 'specific', variables: newSelectedValues } })
        );
      }
    },
    [actionId, allOptionValues.length, dispatch]
  );

  const handleCreateNew = useCallback(
    (value: string) => {
      const sanitized = sanitizeVariableName(value);
      if (!sanitized || selectedValues.includes(sanitized)) {
        return;
      }
      setCreatedVariables((prev) => (prev.includes(sanitized) ? prev : [...prev, sanitized]));
      handleSelectionChange([...selectedValues, sanitized]);
    },
    [handleSelectionChange, selectedValues]
  );

  const onVariableDelete = useCallback(
    (variable: string) => {
      const cleaned = removeDollarSign(variable);
      handleSelectionChange(selectedValues.filter((v) => v !== cleaned));
    },
    [handleSelectionChange, selectedValues]
  );

  const displayChips = useMemo(() => selectedValues.map((v) => `$${v}`), [selectedValues]);

  const selectTrigger = (
    <button
      type="button"
      className="flex w-full items-center justify-between rounded-md border border-stroke bg-background px-3 py-2.5 text-sm ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <span className="truncate text-foreground">{t('dialog.reset.select_variable')}</span>
      <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </button>
  );

  const { setFieldError, clearAlerts } = useDialogAlert({
    id: actionId,
    nodeId: parentNodeId,
    title: t('actions.types.reset'),
    type,
  });

  useEffect(() => {
    if (option === 'specific') {
      const body =
        variables?.length === 0
          ? t('dialog.errors.at_least_one', { 0: t('common.context_variable').toLowerCase() })
          : undefined;

      setFieldError('variables', body);
    } else {
      clearAlerts();
    }
  }, [clearAlerts, option, setFieldError, t, variables?.length]);

  return (
    <ToolkitWrapper type="reset">
      <div className="mb-4">
        <Label className="mb-2">{t('dialog.reset.autocomplete_label')}</Label>
        <MultiSelect
          options={multiSelectOptions}
          selectedValues={selectedValues}
          onSelectionChange={handleSelectionChange}
          placeholder={t('dialog.reset.search_variable')}
          showSelectAll
          selectAllLabel={t('dialog.reset.all_variables_count', { 0: multiSelectOptions.length })}
          groupHeading={t('dialog.reset.autocomplete_label')}
          onCreateNew={handleCreateNew}
          createNewText={(search) => {
            const sanitized = sanitizeVariableName(search);
            return sanitized ? t('dialog.reset.create_variable', { 0: `$${sanitized}` }) : '';
          }}
          trigger={selectTrigger}
        />
      </div>

      {option === 'specific' && variables?.length === 0 && (
        <div className="mb-4">
          <Alert variant="destructive">
            <AlertTriangleIcon size={16} />
            <AlertDescription>
              {t('dialog.errors.at_least_one', { 0: t('common.context_variable').toLowerCase() })}
            </AlertDescription>
          </Alert>
        </div>
      )}

      <Chips className="mt-2" chips={displayChips} onDelete={onVariableDelete} />
    </ToolkitWrapper>
  );
};

export default ToolkitActionReset;
